import {Platform} from 'react-native';
import {
  LESSON_VIEWER_FETCH_START,
  LESSON_VIEWER_FETCH_SUCCESS,
  LESSON_VIEWER_FETCH_ERROR,
  LESSON_VIEWER_RESET,
  LESSON_VIEWER_SET_PHASE,
  LESSON_VIEWER_ADVANCE_TERM,
  LESSON_VIEWER_WORKBOOK_READY,
  LESSON_VIEWER_WORKBOOK_DONE,
  LESSON_VIEWER_WORKBOOK_SUBMIT,
  LESSON_VIEWER_KC_SUBMIT,
  LESSON_VIEWER_KC_NEXT,
  LESSON_VIEWER_KC_SUMMARY_DONE,
  LESSON_VIEWER_MASTERY_SUBMIT,
  LESSON_VIEWER_MASTERY_NEXT,
  LESSON_VIEWER_TOGGLE_DEV_TOOLS,
  LESSON_VIEWER_DEV_JUMP,
  LESSON_VIEWER_SET_TUTORIAL_VIEWED,
  LESSON_VIEWER_SET_TUTOR_CHAT_OPEN,
  LESSON_VIEWER_UNITY_WORLD_LOADING_SET,
} from './actionTypes';
import {fetchLessonContent as fetchFromApi} from 'services/LessonContentService';
import {mapLessonContent} from 'services/LessonContentMapper';

const STORAGE_PREFIX = 'quiklearn_lcv_';

function getTutorialViewed(lessonId) {
  try {
    return localStorage.getItem(`${STORAGE_PREFIX}${lessonId}_tutorial`) === 'true';
  } catch {
    return false;
  }
}

function setTutorialViewedStorage(lessonId) {
  try {
    localStorage.setItem(`${STORAGE_PREFIX}${lessonId}_tutorial`, 'true');
  } catch {
    // silent
  }
}

function saveProgress(lessonId, progress) {
  try {
    localStorage.setItem(
      `${STORAGE_PREFIX}${lessonId}_progress`,
      JSON.stringify(progress),
    );
  } catch {
    // silent
  }
}

function saveTermCompletion(lessonId, termName) {
  try {
    const key = `${STORAGE_PREFIX}${lessonId}_termCompletion`;
    const existing = JSON.parse(localStorage.getItem(key) || '[]');
    if (!existing.includes(termName)) {
      existing.push(termName);
      localStorage.setItem(key, JSON.stringify(existing));
    }
  } catch {
    // silent
  }
}

export const fetchLessonContentAction = (lessonId, token) => async dispatch => {
  dispatch({type: LESSON_VIEWER_FETCH_START});

  try {
    const rawPayload = await fetchFromApi(lessonId, token);
    const viewerModel = mapLessonContent(rawPayload);
    const tutorialViewed = getTutorialViewed(lessonId);

    dispatch({
      type: LESSON_VIEWER_FETCH_SUCCESS,
      payload: {
        rawPayload,
        viewerModel,
        tutorialViewed,
        unityWorldLoading: Platform.OS === 'web',
      },
    });
  } catch (error) {
    dispatch({
      type: LESSON_VIEWER_FETCH_ERROR,
      payload: error.message || 'Failed to load lesson content',
    });
  }
};

export const resetViewer = () => ({type: LESSON_VIEWER_RESET});

export const setLessonViewerUnityWorldLoading = loading => ({
  type: LESSON_VIEWER_UNITY_WORLD_LOADING_SET,
  payload: Boolean(loading),
});

export const setPhase = phase => ({
  type: LESSON_VIEWER_SET_PHASE,
  payload: phase,
});

export const advanceTerm = termIndex => ({
  type: LESSON_VIEWER_ADVANCE_TERM,
  payload: termIndex,
});

export const setTutorialViewed = () => (dispatch, getState) => {
  const lessonId =
    getState().lessonContentViewer.viewerModel?.metadata?.lessonId;
  if (lessonId) {
    setTutorialViewedStorage(lessonId);
  }
  dispatch({type: LESSON_VIEWER_SET_TUTORIAL_VIEWED});
};

export const setWorkbookReady = () => ({type: LESSON_VIEWER_WORKBOOK_READY});
export const setWorkbookDone = () => ({type: LESSON_VIEWER_WORKBOOK_DONE});

export const submitWorkbookAnswer = optionIndex => (dispatch, getState) => {
  const state = getState().lessonContentViewer;
  const term = state.viewerModel?.terms?.[state.activeTermIndex];
  if (!term) return;

  const selectedAnswer = term.workbook.options[optionIndex];
  const correct = selectedAnswer === term.workbook.correctAnswer;
  const timestamp = Date.now();

  dispatch({
    type: LESSON_VIEWER_WORKBOOK_SUBMIT,
    payload: {optionIndex, selectedAnswer, correct, timestamp},
  });
};

export const advancePhase = () => (dispatch, getState) => {
  const state = getState().lessonContentViewer;
  const lessonId = state.viewerModel?.metadata?.lessonId;

  const phaseMap = {
    tutorial: 'frontloading',
    frontloading: 'learning',
    learning: 'workbook_ready',
    workbook_ready: 'workbook_exercise',
    workbook_exercise: 'workbook_options',
    workbook_options: 'knowledge_check',
  };

  const nextPhase = phaseMap[state.phase];
  if (nextPhase) {
    dispatch(setPhase(nextPhase));
    if (lessonId) {
      saveProgress(lessonId, {
        phase: nextPhase,
        activeTermIndex: state.activeTermIndex,
        activeQuestionIndex: 0,
      });
    }
  }
};

export const submitKCAnswer = selectedAnswer => (dispatch, getState) => {
  const state = getState().lessonContentViewer;
  const term = state.viewerModel?.terms?.[state.activeTermIndex];
  if (!term) return;

  const question = term.knowledgeCheck.questions[state.activeQuestionIndex];
  if (!question) return;

  const correct = selectedAnswer === question.correctAnswer;
  const timestamp = Date.now();

  dispatch({
    type: LESSON_VIEWER_KC_SUBMIT,
    payload: {
      questionIndex: state.activeQuestionIndex,
      selectedAnswer,
      correct,
      timestamp,
    },
  });
};

export const advanceKCQuestion = () => (dispatch, getState) => {
  const state = getState().lessonContentViewer;
  const term = state.viewerModel?.terms?.[state.activeTermIndex];
  if (!term) return;

  const totalQuestions = term.knowledgeCheck.questions.length;
  const nextIndex = state.activeQuestionIndex + 1;

  if (nextIndex < totalQuestions) {
    dispatch({type: LESSON_VIEWER_KC_NEXT});
  } else {
    dispatch(setPhase('knowledge_check_summary'));
  }
};

export const kcSummaryDone = () => (dispatch, getState) => {
  const state = getState().lessonContentViewer;
  const lessonId = state.viewerModel?.metadata?.lessonId;
  const term = state.viewerModel?.terms?.[state.activeTermIndex];

  if (term && lessonId) {
    saveTermCompletion(lessonId, term.term);
  }

  dispatch({type: LESSON_VIEWER_KC_SUMMARY_DONE});
};

export const submitMasteryAnswer = selectedAnswer => (dispatch, getState) => {
  const state = getState().lessonContentViewer;
  const questions = state.viewerModel?.mastery?.questions || [];
  const question = questions[state.activeQuestionIndex];
  if (!question) return;

  const correct = selectedAnswer === question.correctAnswer;
  const timestamp = Date.now();

  dispatch({
    type: LESSON_VIEWER_MASTERY_SUBMIT,
    payload: {
      questionIndex: state.activeQuestionIndex,
      selectedAnswer,
      correct,
      timestamp,
    },
  });
};

export const advanceMasteryQuestion = () => ({
  type: LESSON_VIEWER_MASTERY_NEXT,
});

export const toggleDevTools = () => ({type: LESSON_VIEWER_TOGGLE_DEV_TOOLS});

function buildMockAnswers(count, filledCount) {
  return Array.from({length: filledCount}, (_, index) => ({
    questionIndex: index,
    selectedAnswer: null,
    correct: Math.random() > 0.35,
  }));
}

export const jumpToDevTimelineNode = node => (dispatch, getState) => {
  const state = getState().lessonContentViewer;
  const model = state.viewerModel;
  if (!model) return;

  const masteryQuestionCount = model.mastery?.questions?.length || 0;
  const kcQuestionCount =
    model.terms?.[node.termIndex]?.knowledgeCheck?.questions?.length || 0;

  const payload = {
    tutorialViewed: node.phase !== 'tutorial',
    phase: node.phase,
    activeTermIndex: node.termIndex ?? 0,
    activeQuestionIndex: 0,
    workbookReady: false,
    workbookDone: false,
    workbookSubmitted: false,
    workbookSelectedOption: null,
    kcAnswers: [],
    masteryAnswers: [],
    masteryComplete: false,
  };

  if (node.phase === 'workbook_ready') {
    payload.workbookReady = false;
  }

  if (node.phase === 'knowledge_check_summary') {
    payload.kcAnswers = buildMockAnswers(kcQuestionCount, kcQuestionCount);
  }

  if (node.phase === 'mastery') {
    payload.activeQuestionIndex = node.masteryQuestionIndex || 0;
    payload.masteryAnswers = buildMockAnswers(
      masteryQuestionCount,
      node.masteryQuestionIndex || 0,
    );
  }

  if (node.phase === 'results') {
    payload.activeQuestionIndex = Math.max(0, masteryQuestionCount - 1);
    payload.masteryAnswers = buildMockAnswers(
      masteryQuestionCount,
      masteryQuestionCount,
    );
    payload.masteryComplete = true;
  }

  dispatch({
    type: LESSON_VIEWER_DEV_JUMP,
    payload,
  });
};

export const setTutorChatOpen = open => ({
  type: LESSON_VIEWER_SET_TUTOR_CHAT_OPEN,
  payload: open,
});
